import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  ProductListResponse,
  ProductView,
  UpdateProductPayload,
} from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { invalidateSellerCatalog } from "@/lib/query/core/invalidation";
import { queryKeys } from "@/lib/query/core/query-keys";

type UpdateProductInput = {
  storeSlug: string;
  productId: string;
} & UpdateProductPayload;

export function useUpdateProductMutation() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: UpdateProductInput) => {
      const { storeSlug, productId, ...body } = input;
      void storeSlug;
      return apiJsonClient<ProductView>(endpoints.seller.productById(productId), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    },
    onSuccess: async (data, variables) => {
      queryClient.setQueryData(
        queryKeys.seller.productById(variables.storeSlug, variables.productId),
        data,
      );
      queryClient.setQueriesData<ProductListResponse>(
        {
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === "seller" &&
            query.queryKey[1] === variables.storeSlug &&
            query.queryKey[2] === "products" &&
            query.queryKey[3] === "list",
        },
        (current) => {
          if (!current) {
            return current;
          }

          return {
            ...current,
            items: current.items.map((item) =>
              item.id === data.id ? data : item,
            ),
          };
        },
      );
      await invalidateSellerCatalog(queryClient, variables.storeSlug);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.seller.productById(variables.storeSlug, variables.productId),
        exact: true,
      });
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  };
}
